import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Mail, CheckCircle, Loader2 } from 'lucide-react';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setLoading(true);
    try {
      await authAPI.forgotPassword(email);
      setSubmitted(true);
      toast.success('Password reset link sent!');
    } catch (error) {
      // Don't reveal if email exists or not
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', padding: '20px' }}>
        <div style={{ maxWidth: '400px', margin: '0 auto', paddingTop: '60px' }}>
          <div style={{ 
            backgroundColor: 'white', 
            padding: '40px 32px', 
            borderRadius: '16px', 
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            textAlign: 'center'
          }}>
            <div style={{ 
              width: '80px', 
              height: '80px', 
              borderRadius: '50%', 
              backgroundColor: '#dcfce7', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <CheckCircle size={40} color="#16a34a" />
            </div>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#8B0A3A', marginBottom: '12px' }}>
              Check Your Email
            </h1>
            <p style={{ color: '#64748b', marginBottom: '24px', lineHeight: '1.6' }}>
              If an account exists with <strong>{email}</strong>, you will receive a password reset link shortly.
            </p>
            <div style={{ 
              backgroundColor: '#f0f9ff', 
              borderRadius: '12px', 
              padding: '16px',
              marginBottom: '24px'
            }}>
              <p style={{ fontSize: '14px', color: '#0369a1', margin: 0 }}>
                <strong>Tip:</strong> Check your spam folder if you don&apos;t see the email in your inbox.
              </p>
            </div>
            <Link 
              to="/login" 
              style={{ 
                display: 'inline-block',
                padding: '12px 24px',
                backgroundColor: '#8B0A3A',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: 'bold'
              }}
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', padding: '20px' }}>
      <div style={{ maxWidth: '400px', margin: '0 auto', paddingTop: '40px' }}>
        <Link to="/login" style={{ display: 'flex', alignItems: 'center', color: '#64748b', marginBottom: '20px', textDecoration: 'none' }}>
          <ArrowLeft style={{ marginRight: '8px', width: '16px', height: '16px' }} />
          Back to login
        </Link>
        
        <div style={{ 
          backgroundColor: 'white', 
          padding: '32px', 
          borderRadius: '16px', 
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }}>
          <div style={{ 
            width: '64px', 
            height: '64px', 
            borderRadius: '16px', 
            backgroundColor: '#fef3c7', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <Mail size={32} color="#d97706" />
          </div>
          
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#8B0A3A', marginBottom: '8px', textAlign: 'center' }}>
            Forgot Password?
          </h1>
          <p style={{ color: '#64748b', marginBottom: '24px', textAlign: 'center' }}>
            No worries! Enter your email and we&apos;ll send you a reset link.
          </p>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="email" style={{ display: 'block', fontWeight: '500', marginBottom: '8px', color: '#374151' }}>
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                style={{
                  width: '100%',
                  height: '48px',
                  padding: '0 16px',
                  fontSize: '16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '10px',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s'
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                height: '48px',
                backgroundColor: '#f97316',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '14px',
                textTransform: 'uppercase',
                border: 'none',
                borderRadius: '10px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </button>
          </form>

          <p style={{ marginTop: '24px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
            Remember your password?{' '}
            <Link to="/login" style={{ fontWeight: 'bold', color: '#8B0A3A' }}>
              Login here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
